import type {
  EntityDetailResponse,
  FieldSpec,
  QueryRequest,
} from '@moment/horizon-data-access/platform'
import type { SormEntityName } from '../gen/sorm-schema.generated'
import type { BatchQueue } from './batch/batch-queue'
import type { NamedSormQueryBuilder } from './query-builder'
import type { InferPartialRow, InferRow, TypedFieldSpec } from './types'

import { executeSormQuery } from '@moment/horizon-data-access'
import {
  type DataTag,
  type QueryClient,
  type QueryKey,
  queryOptions,
  type UseQueryOptions,
  type UseQueryResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { batchedRefetchInterval } from './batch/batched-refetch-interval'
import { useSormBatchQueue } from './batch/sorm-batch-context'
import { sormEntitySchemaQueryOptions } from './entity-schema'

export type SormAvailableFieldsRow<
  Root extends SormEntityName,
  F extends readonly TypedFieldSpec<Root>[],
> = InferPartialRow<Root, F>

/**
 * Consumer-tunable options for the SORM query hooks: the full react-query
 * observer option set (minus `queryKey`/`queryFn`, which the builder owns) plus
 * the `batch` toggle. Composable — pass `select`, `enabled`, `staleTime`,
 * `placeholderData`, `throwOnError`, `refetchInterval`, etc. exactly as you would
 * to `useQuery`.
 *
 * Typed with three react-query generics on purpose, so `TQueryKey` keeps its
 * generic `QueryKey` default rather than the narrow literal tuple that
 * {@link sormQueryOptions} tags onto `queryKey`. That keeps the option surface
 * variance-free, which is what lets `select`/`TData` compose (see
 * {@link asSormUseQueryOptions}).
 */
export type SormQueryConfig<TQueryFnData, TData = TQueryFnData> = Omit<
  UseQueryOptions<TQueryFnData, Error, TData>,
  'queryKey' | 'queryFn'
> & {
  /**
   * Issue a standalone `/sorm/query` even under a `SormBatchProvider`. Defaults
   * to joining the active batch whenever a provider is mounted above the caller.
   */
  batch?: boolean
}

type SormUseQueryOptions<TQueryFnData, TData> = UseQueryOptions<
  TQueryFnData,
  Error,
  TData
> & { queryKey: DataTag<QueryKey, TQueryFnData, Error> }

/**
 * Launder a merged SORM options object into the type `useQuery` accepts.
 *
 * {@link sormQueryOptions} tags `queryKey` with a narrow literal tuple (via
 * `queryOptions(... as const)`), but the composable {@link SormQueryConfig}
 * surface types its `refetchInterval`/`placeholderData` callbacks against the
 * generic `QueryKey`. TanStack can't unify the two — `queryKey` is invariant
 * while those callbacks are contravariant — so we erase the tuple to the generic
 * `QueryKey` at this single boundary. Mirrors the repo's generated react-query
 * wrappers; `setQueryData`/`getQueryData` stay type-safe through the retained
 * `DataTag`. Private — the only caller is {@link useSormQueryWithConfig}, so the
 * cast lives in exactly one place.
 */
function asSormUseQueryOptions<TQueryFnData, TData>(
  merged: object,
): SormUseQueryOptions<TQueryFnData, TData> {
  return merged as SormUseQueryOptions<TQueryFnData, TData>
}

/**
 * Build a typed `queryOptions` object for the given SORM query builder.
 *
 * Returns the shared configuration (queryKey + queryFn) for the given
 * builder. Compose the rest of react-query's options at the call site via
 * spread — staleTime, select, throwOnError, enabled, etc. all stay where
 * the caller can see them, and the abstraction stays tiny.
 *
 *     const { data } = useQuery({
 *       ...sormQueryOptions(query),
 *       staleTime: 5_000,
 *       select: (rows) => rows[0],
 *     })
 *
 * Works anywhere react-query accepts a typed options object — `useQuery`,
 * `useSuspenseQuery`, `useQueries`, `queryClient.prefetchQuery`,
 * `queryClient.setQueryData`, route loaders, etc. The TanStack
 * `queryOptions` helper preserves the `DataTag` on `queryKey`, so
 * `setQueryData` and friends are fully type-safe without manual generics.
 *
 * This is the low-level building block — React consumers should prefer
 * {@link useSormQuery}, which reads the batch queue from context and aligns
 * polling for them. Use this directly for non-React call sites (route loaders,
 * `queryClient.fetchQuery`, etc.), optionally passing a `queue` to route the
 * request through batching; the `queryKey` is identical either way, so caching
 * and dedup are unaffected.
 *
 * @see https://tkdodo.eu/blog/creating-query-abstractions
 */
export function sormQueryOptions<
  Root extends SormEntityName,
  F extends readonly TypedFieldSpec<Root>[],
>(builder: NamedSormQueryBuilder<Root, F>, queue?: BatchQueue | null) {
  const request = builder.toRequest()

  // `queryName` is intentionally excluded from the queryKey: it's a no-op
  // devtools label, so two otherwise-identical queries should share a cache
  // entry regardless of their label.
  return queryOptions({
    queryKey: [
      'sorm',
      request.root_entity,
      request.context,
      request.fields,
      request.filters,
      request.groups,
      request.scope,
      request.sort,
      request.search,
      request.limit,
      request.offset,
      request.request_params,
      request.object_access_action,
    ] as const,
    queryFn: async (): Promise<InferRow<Root, F>[]> => {
      if (queue) {
        const response = await queue.enqueue(request, builder.getQueryName())
        return (response.data ?? []) as InferRow<Root, F>[]
      }
      const result = await builder.execute()
      return result.data
    },
  })
}

/**
 * Build query options that prune a SORM query's projection to the fields the
 * caller's runtime (per-user) schema exposes — the default engine behind
 * {@link useSormQuery} / {@link useSeededSormQuery}. Filters every declared
 * `.fields(...)` projection against the entity schemas before the query runs, so
 * a caller can ask for the best available columns and a permission-gated field is
 * silently dropped instead of failing the whole query with PERMISSION_DENIED.
 * Rows are therefore `Partial<InferRow>`.
 *
 * Sorts, filters, groups, and scope entries are not pruned. Use
 * {@link sormQueryOptions} (or `useSormQuery(builder, { strictFields: true })`)
 * only when a missing projected field should fail loudly.
 */
export function sormAvailableFieldsQueryOptions<
  Root extends SormEntityName,
  F extends readonly TypedFieldSpec<Root>[],
>(
  builder: NamedSormQueryBuilder<Root, F>,
  queryClient: QueryClient,
  queue?: BatchQueue | null,
) {
  const request = builder.toRequest()

  return queryOptions({
    queryKey: [
      // Same `'sorm'` prefix + full-projection key as the verbatim path: the
      // projection is pruned inside the queryFn, not in the key, so pruned and
      // strict reads of the same builder share one cache entry and the broad
      // `['sorm']` invalidations + exact-match key helpers keep working.
      // Footgun: because the key is shape-agnostic, a strict and a pruned read
      // of the IDENTICAL projection would collide on one entry — the first
      // observer's queryFn wins and the other inherits its row shape. Don't mix
      // strict + pruned reads of the same builder; today none do (each widget
      // owns a distinct projection).
      'sorm',
      request.root_entity,
      request.context,
      request.fields,
      request.filters,
      request.groups,
      request.scope,
      request.sort,
      request.search,
      request.limit,
      request.offset,
      request.request_params,
      request.object_access_action,
    ] as const,
    queryFn: ({ signal }): Promise<SormAvailableFieldsRow<Root, F>[]> =>
      fetchSormAvailableFields({ builder, queryClient, queue, signal }),
  })
}

/**
 * Runs the pruned read that {@link sormAvailableFieldsQueryOptions} caches,
 * without going through the query cache.
 *
 * `fetchQuery` joins a request already in flight for the same key, and no
 * `staleTime` changes that — so a caller that must not read anything older than
 * a write it just made (a read-after-write reconcile, a decision taken on a
 * freshly claimed row) cannot express that through the cache. This is the
 * escape hatch for those reads; everything else should go through the options
 * above so it shares one cache entry.
 */
export async function fetchSormAvailableFields<
  Root extends SormEntityName,
  F extends readonly TypedFieldSpec<Root>[],
>({
  builder,
  queryClient,
  queue,
  signal,
}: {
  builder: NamedSormQueryBuilder<Root, F>
  queryClient: QueryClient
  queue?: BatchQueue | null
  signal?: AbortSignal
}): Promise<SormAvailableFieldsRow<Root, F>[]> {
  const request = builder.toRequest()
  const schemasByEntity = await loadAvailableFieldSchemas(
    request.fields ?? [],
    queryClient,
  )
  const availableRequest = buildAvailableFieldsRequest(request, schemasByEntity)
  if (queue) {
    const response = await queue.enqueue(
      availableRequest,
      builder.getQueryName(),
    )
    return (response.data ?? []) as SormAvailableFieldsRow<Root, F>[]
  }
  const result = await executeSormQuery(availableRequest, {
    queryName: builder.requireQueryName(),
    signal,
  })
  return (result.data?.data ?? []) as SormAvailableFieldsRow<Root, F>[]
}

async function loadAvailableFieldSchemas(
  fields: readonly FieldSpec[],
  queryClient: QueryClient,
): Promise<ReadonlyMap<string, EntityDetailResponse>> {
  const entityNames = new Set(
    fields.flatMap((field) => (field.entity == null ? [] : [field.entity])),
  )
  const entries = await Promise.all(
    [...entityNames].map(async (entityName) => {
      const schema = await queryClient.ensureQueryData(
        sormEntitySchemaQueryOptions(entityName),
      )
      return [entityName, schema] as const
    }),
  )
  return new Map(entries)
}

function buildAvailableFieldsRequest(
  request: QueryRequest,
  schemasByEntity: ReadonlyMap<string, EntityDetailResponse>,
): QueryRequest {
  const fields = (request.fields ?? []).filter((field) =>
    isAvailableField(field, schemasByEntity),
  )
  return { ...request, fields }
}

function isAvailableField(
  field: FieldSpec,
  schemasByEntity: ReadonlyMap<string, EntityDetailResponse>,
): boolean {
  if (field.entity == null || field.field == null) {
    // A computed column (`expr`) names no schema field to check; the server
    // validates the expression's references.
    return true
  }
  const schema = schemasByEntity.get(field.entity)
  return (
    schema?.fields?.some((schemaField) => schemaField.name === field.field) ===
    true
  )
}

/**
 * Shared core for every SORM query hook. Reads the batch queue from context,
 * applies the {@link SormQueryConfig} (the `batch` opt-out + a wall-clock-aligned
 * `refetchInterval`), launders the queryKey variance, and runs `useQuery`. The
 * public hooks differ only in how they build their base options — passed as
 * `buildOptions` — so batching and polling are decided here once and the hooks
 * cannot diverge.
 *
 * A caller-supplied option in `config` (e.g. `placeholderData`) spreads after
 * `buildOptions(queue)`, so it overrides a default the builder set.
 */
export function useSormQueryWithConfig<TQueryFnData, TData>(
  buildOptions: (queue: BatchQueue | null) => object,
  config: SormQueryConfig<TQueryFnData, TData>,
): UseQueryResult<TData, Error> {
  const { batch = true, refetchInterval, ...rest } = config
  const contextQueue = useSormBatchQueue()
  const queue = batch ? contextQueue : null
  return useQuery(
    asSormUseQueryOptions<TQueryFnData, TData>({
      ...buildOptions(queue),
      ...rest,
      refetchInterval: batchedRefetchInterval(queue, refetchInterval),
    }),
  )
}

/**
 * The standard entrypoint for reading a SORM query.
 *
 * By default the projection is pruned to the fields the caller's per-user schema
 * exposes, so a permission-gated field is dropped rather than failing the whole
 * query with PERMISSION_DENIED — rows are `Partial<InferRow>`. Pass
 * `{ strictFields: true }` to send the projection verbatim (failing loudly on a
 * missing field) and get fully-typed `InferRow` rows back — use it only when the
 * projection is known not to be permission-gated.
 *
 * Under a `SormBatchProvider` the request automatically joins the active batch
 * (one `/sorm/batch-query` for the whole subtree); without a provider, or with
 * `{ batch: false }`, it issues a standalone `/sorm/query`. Consumers never
 * touch the queue — pass react-query options (`refetchInterval`, `enabled`,
 * `select`, …) directly; `refetchInterval` is aligned to a shared batch tick so
 * live-poll refetches keep coalescing.
 */
export function useSormQuery<
  Root extends SormEntityName,
  F extends readonly TypedFieldSpec<Root>[],
  TData = InferRow<Root, F>[],
>(
  builder: NamedSormQueryBuilder<Root, F>,
  config: SormQueryConfig<InferRow<Root, F>[], TData> & { strictFields: true },
): UseQueryResult<TData, Error>
export function useSormQuery<
  Root extends SormEntityName,
  F extends readonly TypedFieldSpec<Root>[],
  TData = SormAvailableFieldsRow<Root, F>[],
>(
  builder: NamedSormQueryBuilder<Root, F>,
  config?: SormQueryConfig<SormAvailableFieldsRow<Root, F>[], TData> & {
    strictFields?: false
  },
): UseQueryResult<TData, Error>
export function useSormQuery<
  Root extends SormEntityName,
  F extends readonly TypedFieldSpec<Root>[],
  TData,
>(
  builder: NamedSormQueryBuilder<Root, F>,
  config:
    | (SormQueryConfig<InferRow<Root, F>[], TData> & { strictFields?: boolean })
    | (SormQueryConfig<SormAvailableFieldsRow<Root, F>[], TData> & {
        strictFields?: boolean
      }) = {},
): UseQueryResult<TData, Error> {
  const { strictFields = false, ...rest } = config
  const queryClient = useQueryClient()
  return useSormQueryWithConfig<SormAvailableFieldsRow<Root, F>[], TData>(
    (queue) =>
      strictFields
        ? sormQueryOptions(builder, queue)
        : sormAvailableFieldsQueryOptions(builder, queryClient, queue),
    // The public overloads type `rest` per `strictFields`; erase to the pruned
    // config shape at this single boundary (mirrors `asSormUseQueryOptions`).
    rest as SormQueryConfig<SormAvailableFieldsRow<Root, F>[], TData>,
  )
}
